// components/CustomInput.js
import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import styled from '@mui/system/styled';
import Typography from '@mui/material/Typography';
import { TextField } from '@mui/material';

const InputWrapper = styled('div')(({ theme, error, focused, hasValue }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingTop: '1.5rem',
  paddingBottom: '0.5rem',
  borderBottom: `1px solid ${error ? theme.palette.error.main : 'rgba(0, 0, 0, 0.42)'}`,
  transition: 'border-bottom-color 0.2s ease-in-out',
  '&:focus-within': {
    borderBottomColor: error ? theme.palette.error.main : theme.palette.primary.main,
  },
}));

const StyledInput = styled('input')(({ theme, readOnly }) => ({
  fontSize: '0.875rem',
  fontFamily: theme.typography.fontFamily,
  fontWeight: 400,
  lineHeight: 1.4375,
  color: readOnly ? theme.palette.text.disabled : theme.palette.text.primary, // Gray text
  backgroundColor: 'transparent', // No background
  border: 'none',
  outline: 'none',
  padding: 0,
  width: '100%',
  '&[type="number"]': {
    '-moz-appearance': 'textfield',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  // Add cursor style
  cursor: readOnly ? 'not-allowed' : 'text',
}));

const StyledSelect = styled(TextField)(({ theme, readOnly }) => ({
  '& .MuiInputBase-input': {
    color: readOnly ? theme.palette.text.disabled : theme.palette.text.primary,
    cursor: readOnly ? 'not-allowed' : 'text', // Add cursor here too
  },
  '& .MuiSelect-icon': {
    color: readOnly ? theme.palette.text.disabled : theme.palette.text.secondary,
    cursor: readOnly ? 'not-allowed' : 'pointer', // And on the icon
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none', // Remove the outline
  },
}));

const Label = styled(Typography)(({ theme, focused, hasValue, error, readOnly }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  fontSize: '0.875rem',
  color: error
    ? theme.palette.error.main
    : readOnly
    ? theme.palette.text.disabled // Match label color to text color
    : focused || hasValue
    ? theme.palette.primary.main
    : 'rgba(0, 0, 0, 0.6)',
  transformOrigin: 'top left',
  transform: focused || hasValue || readOnly ? 'translateY(0) scale(0.75)' : 'translateY(1.5rem) scale(1)',
  transition: 'transform 0.2s ease-in-out, color 0.2s ease-in-out',
  pointerEvents: 'none',
}));

const HelperText = styled(Typography)(({ theme, error }) => ({
  fontSize: '0.75rem',
  color: error ? theme.palette.error.main : theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
}));

const CustomInput = forwardRef(
  ({ label, name, value, onChange, error, helperText, type = 'text', select = false, children, ...props }, ref) => {
    const [focused, setFocused] = useState(false);
    const inputRef = useRef(null);
    const { readOnly } = props;

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current.focus();
      },
    }));

    const handleFocus = () => {
      setFocused(true);
    };

    const handleBlur = () => {
      setFocused(false);
    };

    const hasValue = value !== null && value !== undefined && value !== '';

    return (
      <InputWrapper error={!!error} focused={focused} hasValue={hasValue}>
        <Label variant="subtitle1" focused={focused} hasValue={hasValue} error={!!error} readOnly={readOnly}>
          {label}
        </Label>
        {select ? (
          <StyledSelect
            select
            fullWidth
            variant="standard"
            value={value}
            onChange={onChange}
            inputRef={inputRef}
            name={name}
            readOnly={readOnly}
            {...props}
          >
            {children}
          </StyledSelect>
        ) : (
          <StyledInput
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={inputRef}
            readOnly={readOnly}
            {...props}
          />
        )}
        {helperText && (
          <HelperText variant="caption" error={!!error}>
            {helperText}
          </HelperText>
        )}
      </InputWrapper>
    );
  }
);

export default CustomInput;
